import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  CircularProgress,
  IconButton,
  SvgIcon,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import PDFIcon from 'assets/icons/file-pdf.svg';
import PersonReportLazy from 'components/@home/@time-tracking/PersonReport/PersonReportLazy';
import EmployeeAvatar from 'components/@home/EmployeeAvatar';
import Modal from 'components/@home/modals/Modal';
import moment from 'moment-timezone';
import { bool } from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { I18n } from 'utils/i18n';
import companyShape from 'shapes/company';
import employeeShape from 'shapes/employee';
import TimeTrackingCompliance from 'services/api/TimeTrackingCompliance';

const rowSx = theme => ({
  border: 0,
  cursor: 'pointer',
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.secondary.ultraUltraLight,
  },
});

const userSx = {
  display: 'flex',
  alignItems: 'center',
};

const userNameSx = {
  marginLeft: '10px',
};

const xsHiddenSx = theme => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
});

const barSx = theme => ({
  position: 'relative',
  marginRight: '1px',
  backgroundColor: 'rgba(90,97,220,.4)',
  borderRadius: '2px',
  width: 6,
  minHeight: 2,
  '&::after': {
    content: `''`,
    position: 'absolute',
    left: 0,
    bottom: 0,
    display: 'block',
    width: '100%',
    borderRadius: '2px',
    height: 2,
    backgroundColor: theme.palette.secondary.light,
  },
});

const warningBarSx = {
  backgroundColor: 'rgba(220,90,90,0.4)',
};

const barHighlightSx = {
  '&::after': {
    backgroundColor: 'rgba(124,126,151,.9)',
  },
};

const warningBarHighlightSx = {
  '&::after': {
    backgroundColor: 'rgba(151,124,124,.9)',
  },
};

const barsSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-end',
  minHeight: 50,
};

const fileIconSx = {
  fill: theme => theme.palette.primary.main,
};

const pdfShadowSx = {
  maxWidth: 860,
  width: '90vw',
  height: '90vh',
  position: 'relative',
  '&::before': {
    content: `''`,
    position: 'absolute',
    boxShadow: 'inset 0px -10px 10px -10px rgba(0,0,0,0.5)',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    pointerEvents: 'none',
  },
};

const pdfContainerSx = {
  zIndex: 'initial',
  border: 0,
  width: '100%',
  height: '100%',
  '& iframe': {
    zIndex: 'initial',
    border: 0,
    width: '100%',
    height: '100%',
  },
};

const EmployeeRow = ({ employee, company, complianceEnabled = false }) => {
  const [pdfVisible, setPdfVisible] = useState(false);
  const [reports, setReports] = useState([]);
  const [isFetchingReports, setIsFetchingReports] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const { timeTrackerLog } = employee;
  const date = moment(timeTrackerLog.month).tz('UTC');
  const year = date.year();
  const month = date.month();
  const days = new Array(date.daysInMonth()).fill(null);
  const { max, total } = Object.keys(timeTrackerLog.days || {}).reduce(
    ({ max: maxIn, total: totalIn }, key) => {
      const day = timeTrackerLog.days[key] || { totalHours: 0 };
      return {
        max: Math.max(day.totalHours || 0, maxIn),
        total: totalIn + (day.totalHours || 0),
      };
    },
    {
      max: 0,
      total: 0,
    },
  );
  const dayValues = days.map((d, idx) => {
    const day = idx + 1;
    const currDay = timeTrackerLog.days?.[day];
    const time = currDay?.totalHours || 0;
    const shifts = currDay?.shifts || [];
    const warning = currDay?.shifts?.some(s => s.warning);
    const hours = Math.floor(time);
    const minutes = Math.round((time - hours) * 60);
    const height = max ? (time / max) * 50 : 0;
    return {
      day,
      hours,
      minutes,
      height,
      warning,
      shifts,
    };
  });

  const toggleModal = open => () => {
    setPdfVisible(open);
  };

  const totalHours = Math.floor(total);
  const totalMinutes = Math.round((total - totalHours) * 60);

  useEffect(() => {
    if (!complianceEnabled) {
      setReports([]);
      setDownloadError('');
      return () => {};
    }

    let cancelled = false;
    const fetchReports = async () => {
      setIsFetchingReports(true);
      setDownloadError('');
      try {
        const employeeReports = await TimeTrackingCompliance.listEmployeeReports(employee._id);
        if (!cancelled) {
          setReports(employeeReports);
        }
      } catch (error) {
        console.error('Failed to load time tracking reports', error);
        if (!cancelled) {
          setReports([]);
        }
      } finally {
        if (!cancelled) {
          setIsFetchingReports(false);
        }
      }
    };

    fetchReports();

    return () => {
      cancelled = true;
    };
  }, [complianceEnabled, employee._id]);

  const monthKey = date.format('YYYY-MM');
  const hasComplianceReport =
    complianceEnabled && reports.some(report => report.month === monthKey);

  useEffect(() => {
    setDownloadError('');
  }, [monthKey]);

  const downloadReport = useCallback(async () => {
    try {
      setIsDownloading(true);
      setDownloadError('');
      const pdfUrl = await TimeTrackingCompliance.getReportDownloadUrl(employee._id, monthKey);
      if (pdfUrl) {
        window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error('Missing PDF URL');
      }
    } catch (error) {
      console.error('Failed to download compliance report', error);
      setDownloadError(I18n.t('TimeTracking.PersonReport.DownloadError'));
    } finally {
      setIsDownloading(false);
    }
  }, [employee._id, monthKey]);

  return (
    <TableRow key={employee._id} hover sx={rowSx}>
      <TableCell>
        <Box sx={userSx}>
          <EmployeeAvatar employee={employee} />
          <Typography sx={userNameSx}>{employee.name}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography>{employee.code}</Typography>
      </TableCell>
      <TableCell>
        <Typography>{employee.department}</Typography>
      </TableCell>
      <TableCell sx={xsHiddenSx}>
        <Typography>{employee.jobTitle}</Typography>
      </TableCell>
      <TableCell>{employee.location}</TableCell>
      <TableCell>{`${totalHours}h ${totalMinutes}m`}</TableCell>
      <TableCell>
        <Box sx={barsSx}>
          {dayValues.map(({ day, height, hours, minutes, warning }) => (
            <Box
              sx={[
                barSx,
                warning && warningBarSx,
                height >= 2 && (warning ? warningBarHighlightSx : barHighlightSx),
              ]}
              key={day}
              style={{ height }}
              data-tooltip-id="time-tracking-day"
              data-tooltip-content={`${moment(new Date(year, month, day)).format(
                'LL',
              )} - ${hours}h ${minutes}m`}
            />
          ))}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!complianceEnabled ? (
            // Compliance mode OFF: Show "View PDF" button (in-browser preview)
            <Tooltip title={I18n.t(`TimeTracking.PersonReport.SeeReport`)}>
              <Button onClick={toggleModal(true)}>
                <SvgIcon sx={fileIconSx}>
                  <PDFIcon />
                </SvgIcon>
              </Button>
            </Tooltip>
          ) : (
            // Compliance mode ON: Show "Download" button only if report exists
            <>
              {isFetchingReports ? (
                <CircularProgress size={20} />
              ) : (
                hasComplianceReport && (
                  <Tooltip title={I18n.t('TimeTracking.PersonReport.DownloadTooltip')}>
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={downloadReport}
                        disabled={isDownloading}
                      >
                        {isDownloading ? <CircularProgress size={18} /> : <DownloadIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )
              )}
            </>
          )}
        </Box>
        {downloadError && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {downloadError}
          </Typography>
        )}
        <Modal isOpen={pdfVisible} onClose={toggleModal(false)}>
          <Box sx={pdfShadowSx}>
            <Box sx={pdfContainerSx}>
              <PersonReportLazy
                pdfViewerClass=""
                date={date}
                companyName={company.name}
                employee={employee}
                dayValues={dayValues}
              />
            </Box>
          </Box>
        </Modal>
      </TableCell>
    </TableRow>
  );
};

EmployeeRow.propTypes = {
  company: companyShape.isRequired,
  employee: employeeShape.isRequired,
  complianceEnabled: bool,
};

export default EmployeeRow;
